<template>
  <div>
    <v-container>
      <base-material-card icon="mdi-robot-happy-outline" title="สถิติคำถามแชทบอท (ผู้ช่วย HRVEC)" class="px-5 py-3">
        <v-row align="center" class="mb-2">
          <v-col cols="12" sm="4" md="3">
            <v-select
              v-model="days"
              :items="dayOptions"
              label="ช่วงเวลา"
              dense
              outlined
              hide-details
              @change="load"
            ></v-select>
          </v-col>
          <v-col cols="auto">
            <v-btn color="primary" outlined :loading="loading" @click="load">
              <v-icon left>mdi-refresh</v-icon>รีเฟรช
            </v-btn>
          </v-col>
        </v-row>

        <v-alert v-if="error" type="warning" outlined>{{ error }}</v-alert>

        <template v-else>
          <v-row class="mb-2">
            <v-col v-for="card in totalCards" :key="card.label" cols="6" sm="4" md>
              <v-card :color="card.color" dark>
                <v-card-text class="text-center">
                  <div class="text-h4">{{ card.value }}</div>
                  <div>{{ card.label }}</div>
                </v-card-text>
              </v-card>
            </v-col>
          </v-row>

          <v-alert type="info" text dense class="mb-4">
            คำถามที่ "ตอบไม่ได้" คือแหล่งข้อมูลหลักในการเพิ่ม keyword/คำตอบใหม่ — แก้ไขที่ไฟล์
            <code>HRvec/src/chatbot/knowledgeBase.js</code> แล้ว build ใหม่
          </v-alert>

          <v-row>
            <v-col cols="12" md="6">
              <h3 class="mb-2">คำถามที่บอทตอบไม่ได้</h3>
              <v-data-table
                :headers="unansweredHeaders"
                :items="summary.unanswered"
                :items-per-page="10"
                :loading="loading"
                dense
                class="elevation-1"
                no-data-text="ยังไม่มีข้อมูล"
              >
                <template v-slot:item.last_asked="{ item }">{{ formatDate(item.last_asked) }}</template>
              </v-data-table>
            </v-col>
            <v-col cols="12" md="6">
              <h3 class="mb-2">ประเด็นที่ถามบ่อย</h3>
              <v-data-table
                :headers="topHeaders"
                :items="topItems"
                :items-per-page="10"
                :loading="loading"
                dense
                class="elevation-1"
                no-data-text="ยังไม่มีข้อมูล"
              ></v-data-table>
            </v-col>
            <v-col cols="12" md="8">
              <h3 class="mb-2">คำตอบที่ผู้ใช้กด "ไม่ช่วย"</h3>
              <v-data-table
                :headers="negativeHeaders"
                :items="negativeItems"
                :items-per-page="10"
                :loading="loading"
                dense
                class="elevation-1"
                no-data-text="ยังไม่มีข้อมูล"
              >
                <template v-slot:item.created_at="{ item }">{{ formatDate(item.created_at) }}</template>
              </v-data-table>
            </v-col>
            <v-col cols="12" md="4">
              <h3 class="mb-2">กลุ่มผู้ใช้ที่ถาม</h3>
              <v-simple-table dense class="elevation-1">
                <tbody>
                  <tr v-for="row in summary.roles" :key="row.user_role">
                    <td>{{ roleLabel(row.user_role) }}</td>
                    <td class="text-right">{{ row.cnt }}</td>
                  </tr>
                  <tr v-if="!summary.roles.length">
                    <td class="grey--text">ยังไม่มีข้อมูล</td>
                  </tr>
                </tbody>
              </v-simple-table>
            </v-col>
          </v-row>
        </template>
      </base-material-card>
    </v-container>
  </div>
</template>

<script>
/**
 * chatbot_insights.vue — สถิติจาก chatbot_log (ดู chatbot_log.summary.php / MIGRATION_chatbot_log.sql)
 * ใช้ดูว่าผู้ใช้ถามอะไรบ่อย และคำถามใดที่ผู้ช่วยยังตอบไม่ได้ เพื่อนำไปเพิ่มใน src/chatbot/knowledgeBase.js
 */
import { ROLE_LABELS } from "@/chatbot/knowledgeBase";
import { getIntent } from "@/chatbot/chatEngine";

const intentTitle = id => {
  const intent = getIntent(id);
  return intent ? intent.title : `${id} (ถูกลบจากฐานความรู้แล้ว)`;
};

export default {
  name: "ChatbotInsights",
  data() {
    return {
      days: 30,
      dayOptions: [
        { text: "7 วันล่าสุด", value: 7 },
        { text: "30 วันล่าสุด", value: 30 },
        { text: "90 วันล่าสุด", value: 90 },
        { text: "1 ปี", value: 365 }
      ],
      loading: false,
      error: "",
      summary: { totals: {}, top: [], unanswered: [], negative: [], roles: [] },
      unansweredHeaders: [
        { text: "คำถาม", value: "question" },
        { text: "จำนวน", value: "cnt", align: "end", width: 80 },
        { text: "ล่าสุด", value: "last_asked", width: 130 }
      ],
      topHeaders: [
        { text: "ประเด็น", value: "title" },
        { text: "จำนวน", value: "cnt", align: "end", width: 80 },
        { text: "👍", value: "helpful", align: "end", width: 60 },
        { text: "👎", value: "not_helpful", align: "end", width: 60 }
      ],
      negativeHeaders: [
        { text: "คำถาม", value: "question" },
        { text: "คำตอบที่บอทให้", value: "title" },
        { text: "เวลา", value: "created_at", width: 130 }
      ]
    };
  },
  computed: {
    totalCards() {
      const t = this.summary.totals;
      return [
        { label: "คำถามทั้งหมด", value: t.total || 0, color: "primary" },
        { label: "ตอบไม่ได้", value: t.unanswered || 0, color: "warning" },
        { label: "ช่วยได้ 👍", value: t.helpful || 0, color: "success" },
        { label: "ไม่ช่วย 👎", value: t.not_helpful || 0, color: "error" },
        { label: "ถูกเตือนข้อมูลส่วนบุคคล", value: t.pii_blocked || 0, color: "blue-grey" }
      ];
    },
    topItems() {
      return this.summary.top.map(row => ({ ...row, title: intentTitle(row.intent_id) }));
    },
    negativeItems() {
      return this.summary.negative.map(row => ({ ...row, title: row.intent_id ? intentTitle(row.intent_id) : "-" }));
    }
  },
  mounted() {
    this.load();
  },
  methods: {
    async load() {
      this.loading = true;
      this.error = "";
      try {
        const res = await this.$http.post("chatbot_log.summary.php", { days: this.days });
        if (res.data && res.data.status) {
          this.summary = res.data;
        } else {
          this.error = (res.data && res.data.message) || "ไม่สามารถโหลดข้อมูลได้";
        }
      } catch (e) {
        if (e.isSessionExpired) return;
        this.error =
          (e.response && e.response.data && e.response.data.message) || "ไม่สามารถโหลดข้อมูลได้ กรุณาลองใหม่อีกครั้ง";
      } finally {
        this.loading = false;
      }
    },
    roleLabel(role) {
      return ROLE_LABELS[role] || role;
    },
    formatDate(value) {
      return value ? this.$moment(value).add(543, "years").format("D MMM YYYY HH:mm") : "";
    }
  }
};
</script>
